import { yupResolver } from '@hookform/resolvers/yup'
import { HttpStatusCode } from 'axios'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { useUser } from '@/api/models/common'
import {
  BalanceAlias,
  PaymentActionType,
  PaymentCard,
  PaymentDetail,
  PaymentDetailInvoice,
  PaySystem
} from '@/api/models/finance'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import SomethingWentWrong from '@/components/pages/plugs/SomethingWentWrong'
import { AppButton } from '@/components/shared/button'
import { ButtonSkeleton } from '@/components/shared/skeleton'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { formatNumberLocale, separateThousands } from '@/utils/number'

import { receiptMethodsOptions } from '../../../BillsPayDrawer/BillsPayDrawer.constants'
import QrCodeForm from '../../../BillsPayDrawer/QrCodeForm'
import { usePay } from '../../../hooks/usePay'
import { usePremises } from '../../../hooks/usePremises'
import { PaymentFormData, paymentSchema } from './model/schemas'
import { PaymentForm } from './PaymentForm'
import { ReceiptCard } from './ReceiptCard'

type Props = {
  balanceAlias: BalanceAlias
  showErrorModal: () => void
  onBackClick: () => void
  paymentDetail?: {
    payment?: PaymentDetail
    isLoading: boolean
    errorStatus: HttpStatusCode
  }
  returnBackUrl?: string
}

export const Payment = ({
  balanceAlias,
  showErrorModal,
  onBackClick,
  paymentDetail,
  returnBackUrl
}: Props) => {
  const { reachGoal } = useYandexMetrika()
  const { getPremiseNameById } = usePremises()
  const { data: user } = useUser()
  const { payment, isLoading, errorStatus } = paymentDetail ?? {
    payment: undefined,
    isLoading: false,
    error: null
  }

  const formattedTotalSum = formatNumberLocale(
    separateThousands(payment?.totalSum || 0)
  )

  const [selectedPayMethod, setSelectedPayMethod] = useState<
    PaymentCard | undefined
  >(undefined)

  const isTotalPayment =
    (balanceAlias === BalanceAlias.CapitalRepairs ||
      balanceAlias === BalanceAlias.Utility) &&
    payment?.balances &&
    payment?.balances?.length === 1

  const isPrepayment =
    (balanceAlias === BalanceAlias.ComplementaryServices &&
      (payment?.totalSum ?? 0) <= 0) ||
    isTotalPayment ||
    false

  useEffect(() => {
    if (!selectedPayMethod) {
      setSelectedPayMethod(payment?.cards[0])
    }
  }, [payment?.cards, selectedPayMethod])

  const paymentForm = useForm<PaymentFormData>({
    defaultValues: {
      amount:
        balanceAlias === BalanceAlias.ComplementaryServices
          ? undefined
          : formattedTotalSum,
      email: user?.email
    },
    resolver: yupResolver(paymentSchema),
    context: { isPrepayment },
    mode: 'onSubmit'
  })

  const isComplimentaryServicesBalanceSelected =
    balanceAlias === BalanceAlias.ComplementaryServices

  useEffect(() => {
    if (payment && !isLoading) {
      paymentForm.reset({
        amount:
          balanceAlias === BalanceAlias.ComplementaryServices
            ? undefined
            : formattedTotalSum,
        email: user?.email
      })
    }
  }, [payment, isLoading, paymentForm, balanceAlias, user])

  const getPaymentInvoices = (): PaymentDetailInvoice[] => {
    const items = isComplimentaryServicesBalanceSelected
      ? payment?.invoices
      : payment?.balances

    return (items ?? []).map(item => {
      const isBalance = 'personalAccountId' in item

      return {
        id: isBalance ? item.personalAccountId : item.id,
        premiseName:
          (isBalance
            ? getPremiseNameById(item.personalAccountId)
            : item.premiseName) ?? '',
        fileUrl: isBalance ? item.latestInvoiceFile?.url : item.fileUrl,
        name: item.name,
        totalSum: item.totalSum
      }
    })
  }

  const getPaymentActionType = () => {
    if (isComplimentaryServicesBalanceSelected) {
      return getPaymentInvoices().length === 1
        ? PaymentActionType.Single
        : PaymentActionType.Multiply
    }
    return PaymentActionType.Balance
  }

  const { makePayment } = usePay({
    merchId: payment?.merchId ?? '',
    invoiceIds: isComplimentaryServicesBalanceSelected
      ? getPaymentInvoices().map(invoice => invoice.id)
      : [],
    receiptMethod: receiptMethodsOptions[0].key,
    paymentActionType: getPaymentActionType(),
    balances: isComplimentaryServicesBalanceSelected
      ? []
      : payment?.balances?.map(balance => ({
          alias: balance.alias,
          personalAccountId: balance.personalAccountId,
          amount: balance.totalSum
        })) || [],
    returnBackUrl
  })

  const sendMetrics = (amount: number) => {
    if (payment?.totalSum) {
      if (amount > payment?.totalSum) {
        reachGoal(YandexMetrikaEvent.INV2_PAY_DEBT_MORE)
      } else if (amount < payment?.totalSum) {
        reachGoal(YandexMetrikaEvent.INV2_PAY_DEBT_LESS)
      } else {
        reachGoal(YandexMetrikaEvent.INV2_PAY_DEBT_EQUAL)
      }
    }
  }

  const handleProcceedPay = (formData: PaymentFormData) => {
    makePayment({
      ...formData,
      amount:
        !isPrepayment && (!formData.amount || Number(formData.amount) === 0)
          ? String(payment?.totalSum)
          : formData.amount,
      cardId: selectedPayMethod?.number && selectedPayMethod?.id
    })

    if (formData.amount) {
      sendMetrics(Number(formData.amount))
    }
  }

  useEffect(() => {
    if (errorStatus === HttpStatusCode.Locked) {
      showErrorModal()
    }
  }, [errorStatus, showErrorModal])

  if (errorStatus && errorStatus !== HttpStatusCode.Locked) {
    return <SomethingWentWrong buttonName="Назад" onButtonClick={onBackClick} />
  }

  return (
    <div className="mb-20 flex h-full flex-col justify-between overflow-auto sm:-mx-16 sm:px-16">
      <div>
        <ReceiptCard
          isLoading={isLoading}
          invoices={getPaymentInvoices()}
          totalSum={payment?.totalSum ?? 0}
          name={payment?.name ?? ''}
          balanceAlias={balanceAlias}
          premiseName={payment?.premiseName ?? ''}
        />
        <FormProvider {...paymentForm}>
          <PaymentForm
            cards={payment?.cards || []}
            isLoading={isLoading}
            isPrepayment={isPrepayment}
            urlDesc={payment?.urlDesc || ''}
            urlOffer={payment?.urlOffer || ''}
            selectedCard={selectedPayMethod}
            onCardSelect={setSelectedPayMethod}
            showEmailBlock={selectedPayMethod?.paySystem !== PaySystem.Qr}
          />
        </FormProvider>
        {selectedPayMethod?.paySystem === PaySystem.Qr && (
          <div className="mx-40 mt-32 lg:mx-24 sm:mx-0 xxs:mt-20">
            <QrCodeForm qrContent={payment?.invoices?.[0]?.qrContent} />
          </div>
        )}
      </div>
      {isLoading && (
        <div className="px-40 lg:px-20 sm:p-0">
          <ButtonSkeleton className="h-56 w-full px-40 xxs:h-[44px]" />
        </div>
      )}
      {!isLoading && selectedPayMethod?.paySystem === PaySystem.Qr ? (
        <></>
      ) : (
        <AppButton
          className="mx-40 mt-16 lg:mx-24 xxs:mx-0"
          onClick={paymentForm.handleSubmit(handleProcceedPay)}
        >
          Оплатить банковской картой
        </AppButton>
      )}
    </div>
  )
}
